import { CSSProperties } from "react";
import { TCustomLandingButtonStyle, TDesignStyle } from "ui";
import { ButtonProps } from "ui/v2/Button";

import { API_ORGANIZATION } from "@/constants/_api";

import request from "../requestWithToken";

import { JobApplicationMandatoryData } from "./jobVacancies";

type SectionList = "header" | "footer" | "job_list";
type SectionConfig = { mode?: "dark" | "light"; custom_color?: string };

export type SectionImageItem =
  | string
  | { src: string; link?: string; tracker?: string }
  | { type: "carousel"; items: (string | { src: string; link?: string; tracker?: string })[] };

export enum OrganizationIdentifier {
  LearningWithLR = "learningwithlr",
}

export type ManagedJobVacancyColumn = {
  key: string;
  label: string;
  visible: boolean;
  sortable: boolean;
};

export type Organization = {
  id: number;
  name?: string | null;
  logo_url?: string | null;
  white_logo_url?: string | null;
  sublogo_url?: string;
  identifier?: OrganizationIdentifier | string;
  favicon?: string | null;
  favicon_32?: string | null;
  favicon_16?: string | null;
  web_title?: string | null;
  web_description?: string | null;
  apple_touch?: string | null;
  webmanifest?: string | null;
  index_path: string;
  twitter_url: string;
  instagram_url: string;
  linkedin_url: string;
  facebook_url: string;
  youtube_url: string;
  tiktok_url: string;
  corporation_name?: string;
  career_logo?: string;
  career_logo_hover?: string;
  career_index_path: string;
  career_web_title?: string | null;
  career_instagram_url?: string;
  gtm_id?: string;
  apply_job: {
    alert: {
      text: string;
    };
  };
  themes?: {
    colors?: {
      primary?: string;
      secondary?: string;
    };
  };
  job_application_mandatory_data: JobApplicationMandatoryData[];
  design_style: TDesignStyle;
  company_web_url: string;
  landing_page_attributes: {
    hero_variant?: "full_background" | "default";
    hero_supporters?: {
      text?: string;
      image?: string;
      imageClassName?: string;
      imageStyle?: { mobile?: CSSProperties; desktop?: CSSProperties };
    }[];
    hero_headline_style?: {
      desktop?: CSSProperties;
      mobile?: CSSProperties;
    };
    hero_description_style?: CSSProperties;
    alert_info?: {
      enabled: boolean;
      alert_text_color: string;
      alert_bg_color: string;
      button?: {
        label: string;
        url: string;
        icon?: string;
        style?: {
          backgroundColor?: string;
          textColor?: string;
          borderColor?: string;
        };
      };
    };
    alert_text_color?: string;
    alert_text: string;
    alert_buttons:
      | { text?: string; onClick: () => void }[]
      | { text?: string; onClick: () => void };
    job_list_button_icon: string;
    job_list_button_icon_disabled: string;
    faq_link: string;
    hero_images: string[];
    hero_images_mobile: string[];
    hero_logo?: {
      urls: string[];
      size?: {
        desktop?: { width?: string };
        mobile?: { width?: string };
      };
    };
    hero_bg?: {
      src: string;
      style?: CSSProperties;
    };
    font_style?: string;
    button_style?: TCustomLandingButtonStyle;
    section_nav_buttons?: { label: string; id: string; label_id?: string }[];
    section_before_job_list: SectionImageItem[];
    section_after_job_list: SectionImageItem[];
    section_before_job_list_mobile: SectionImageItem[];
    section_after_job_list_mobile: SectionImageItem[];
    section_before_job_list_en: SectionImageItem[];
    section_after_job_list_en: SectionImageItem[];
    section_before_job_list_mobile_en: SectionImageItem[];
    section_after_job_list_mobile_en: SectionImageItem[];
    video_url?: string;
    video_thumbnail_url?: string;
    company_alias?: string;
    outlined_button_variant?: ButtonProps["variant"];
    hide_apply_button_mode?: boolean;
    section_theme?: Record<SectionList, SectionConfig>;
    carousel?: {
      title_className?: string;
    };
    faq_section?: {
      enabled: boolean;
      title_className?: string;
      items?: {
        question: string;
        answer: string;
      }[];
    };
    multi_language?: boolean;
    logo_marquee?: {
      enabled: boolean;
      logos?: string[][];
    };
    job_list?: {
      enabled: boolean;
    };
    hero_apply_button_url?: string | { pathname: string };
    show_apply_button_icon?: boolean;
    hide_auth_buttons?: boolean;
    hide_footer_address?: boolean;
    load_more_vacancy_button?: boolean;
  };
  managed_job_vacancy_columns: {
    general: ManagedJobVacancyColumn[];
    assessment: ManagedJobVacancyColumn[];
    submission_pipeline: ManagedJobVacancyColumn[];
  };
  job_grades: {
    value: string;
    label: string;
    order_level: number;
  }[];
  department_names: {
    value: string;
    label: string;
    order_level: number;
  }[];
  organization_roles: string[];
  organization_levels: string[];
  organization_types: string[];
  team_sizes: string[];
  massive_hiring: {
    enabled: boolean;
    registration_dates: {
      ends: string;
      starts: string;
    };
    show_timer_countdown: boolean;
    timer_theme: "dark" | "light";
  };
  ai_cv_template: string;
};

export const fetchOrganization = () =>
  request<Organization>({ url: API_ORGANIZATION, httpMethod: "GET" });
